import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import { Input } from '@amore/ui/components/input'
import { InputAddon, InputGroup } from '@amore/ui/components/input-group'
import { Select, SelectItem } from '@amore/ui/components/select'
import { Textarea } from '@amore/ui/components/textarea'
import { useNavigate } from '@tanstack/react-router'
import { BadgeCheck, Banknote, FolderOpen, NotebookPen, Trash2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { formatMoney, newId } from '@/lib/format'
import { supplierCategoryLabels } from '@/lib/labels'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { Expense, Payment, WeddingData } from '@/mock/types'
import { Segmented } from '@/patterns/detail'
import { DetailSheet } from '@/patterns/dialog'
import { LinkedRecord } from '@/patterns/linked-record'
import { categoryLabel, categoryOrder } from '@/screens/suppliers/model'
import { useToast } from '@/shell/toaster'
import { DeleteExpenseDialog } from './budget-dialogs'
import { syncPaymentTasks } from './model'
import { PaymentsTab } from './payments-tab'

export const expenseTabs = ['item', 'payments'] as const
export type ExpenseTab = (typeof expenseTabs)[number]

const tabLabels: Record<ExpenseTab, string> = { item: 'Položka', payments: 'Platby' }

const NO_SUPPLIER = '__none'

type Draft = {
  title: string
  amount: string
  estimate: boolean
  category: string
  supplierId: string | undefined
  note: string
  payments: Payment[]
}

const draftOf = (expense: Expense): Draft => ({
  title: expense.title,
  amount: expense.amount ? String(expense.amount) : '',
  estimate: expense.estimate,
  category: expense.category,
  supplierId: expense.supplierId,
  note: expense.note ?? '',
  payments: expense.payments,
})

const sameDraft = (a: Draft, b: Draft) =>
  (Object.keys(a) as (keyof Draft)[]).every((key) =>
    key === 'payments' ? JSON.stringify(a[key]) === JSON.stringify(b[key]) : a[key] === b[key],
  )

const parseAmount = (value: string) => Number(value.replace(/\D/g, '')) || 0

const softControl = 'h-10 border-transparent bg-muted/60 hover:bg-muted'

/**
 * One sheet for adding and editing a budget item, in two tabs: Položka (price
 * and whether it's agreed or a guess, category, the supplier from My
 * suppliers, note) and Platby (the payment plan). Without `expense` it adds
 * one; `supplierId` prefills it from My suppliers.
 */
export function ExpenseSheet({
  expense: existing,
  supplierId,
  initialTab = 'item',
  onClose,
}: {
  expense?: Expense
  supplierId?: string
  initialTab?: ExpenseTab
  onClose: () => void
}) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const isNew = !existing
  const expense: Expense = existing ?? {
    id: 'new',
    title: '',
    category: data.suppliers.find((item) => item.id === supplierId)?.category ?? 'other',
    amount: 0,
    estimate: true,
    supplierId,
    payments: [],
  }
  const [draft, setDraft] = useState(() => draftOf(expense))
  const [tab, setTab] = useState<ExpenseTab>(initialTab)
  const [titleError, setTitleError] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))
  const supplier = data.suppliers.find((item) => item.id === draft.supplierId)
  const dirty = !sameDraft(draft, draftOf(expense))

  const save = () => {
    const title = supplier?.name ?? draft.title.trim()
    if (!title) {
      setTitleError(true)
      setTab('item')
      return
    }
    const saved: Expense = {
      ...expense,
      id: isNew ? newId('e') : expense.id,
      title,
      amount: parseAmount(draft.amount),
      estimate: draft.estimate,
      category: supplier?.category ?? draft.category,
      supplierId: draft.supplierId,
      note: draft.note.trim() || undefined,
      // An untouched new payment card (no name, no amount) isn't worth keeping.
      payments: draft.payments
        .filter((payment) => payment.name.trim() || payment.amount > 0)
        .map((payment) => ({ ...payment, name: payment.name.trim() || 'Platba' })),
    }
    update((current) =>
      syncPaymentTasks(
        {
          ...current,
          budget: {
            ...current.budget,
            expenses: isNew
              ? [...current.budget.expenses, saved]
              : replaceById(current.budget.expenses, expense.id, saved),
          },
        },
        saved,
        title,
      ),
    )
    showToast({
      title: isNew ? 'Položka přidána' : 'Položka uložena',
      description: title,
      tone: 'success',
    })
    onClose()
  }

  return (
    <>
      <DetailSheet
        open
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        eyebrow={isNew ? 'Nová položka' : 'Položka rozpočtu'}
        title={supplier?.name ?? (expense.title || 'Nová položka')}
        dirty={dirty}
        titleContent={
          <div className="space-y-4">
            <div className="space-y-1">
              {supplier ? (
                <p className="font-heading text-[1.75rem] leading-tight tracking-tight">
                  {supplier.name}
                </p>
              ) : (
                <textarea
                  value={draft.title}
                  rows={1}
                  data-autofocus={isNew || undefined}
                  readOnly={!canEdit}
                  aria-label="Název položky"
                  aria-invalid={titleError || undefined}
                  placeholder={isNew ? 'Za co budete platit?' : 'Název položky'}
                  onChange={(event) => {
                    set({ title: event.target.value })
                    setTitleError(false)
                  }}
                  className={cn(
                    'field-sizing-content -mx-2 w-[calc(100%+1rem)] resize-none rounded-lg bg-transparent px-2 py-1 font-heading text-[1.75rem] leading-tight tracking-tight outline-none transition-colors',
                    canEdit && 'hover:bg-muted/60 focus:bg-muted/60',
                  )}
                />
              )}
              {titleError ? (
                <p className="text-sm text-error-foreground">Doplňte název položky.</p>
              ) : null}
            </div>
            <div role="tablist" aria-label="Části položky" className="-mb-5 flex gap-7 pt-1">
              {expenseTabs.map((value) => {
                const current = value === tab
                const count = value === 'payments' ? draft.payments.length : 0
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={current}
                    onClick={() => setTab(value)}
                    className={cn(
                      '-mb-px inline-flex items-center gap-1.5 border-b-2 pb-3 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                      current
                        ? 'border-action text-action'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {tabLabels[value]}
                    {count > 0 ? (
                      <span className="text-xs font-medium text-muted-foreground tabular-nums">
                        {count}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          </div>
        }
        primaryAction={
          canEdit ? { label: isNew ? 'Přidat položku' : 'Uložit', onClick: save } : undefined
        }
        destructiveAction={
          canEdit && !isNew
            ? {
                label: 'Smazat položku',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmDelete(true),
              }
            : undefined
        }
      >
        {tab === 'item' ? (
          <ItemTab
            expense={expense}
            draft={draft}
            data={data}
            canEdit={canEdit}
            set={set}
            onOpenSupplier={(id) => void navigate({ to: `/suppliers/${id}` })}
          />
        ) : (
          <PaymentsTab
            amount={parseAmount(draft.amount)}
            payments={draft.payments}
            tasks={data.tasks}
            canEdit={canEdit}
            onChange={(payments) => set({ payments })}
            onOpenItem={() => setTab('item')}
          />
        )}
      </DetailSheet>
      {existing ? (
        <DeleteExpenseDialog
          expense={existing}
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          onDeleted={onClose}
        />
      ) : null}
    </>
  )
}

function ItemTab({
  expense,
  draft,
  data,
  canEdit,
  set,
  onOpenSupplier,
}: {
  expense: Expense
  draft: Draft
  data: WeddingData
  canEdit: boolean
  set: (patch: Partial<Draft>) => void
  onOpenSupplier: (id: string) => void
}) {
  const supplier = data.suppliers.find((item) => item.id === draft.supplierId)
  const paid = draft.payments.reduce((sum, item) => sum + (item.paid ? item.amount : 0), 0)
  const amount = parseAmount(draft.amount)
  // A supplier belongs to one budget item; the ones already in the budget aren't offered again.
  const available = data.suppliers.filter(
    (item) =>
      item.status !== 'cancelled' &&
      !data.budget.expenses.some(
        (other) => other.id !== expense.id && other.supplierId === item.id,
      ),
  )

  return (
    <div className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1">
      <fieldset disabled={!canEdit} className="grid grid-cols-2 gap-x-6 gap-y-7">
        <legend className="sr-only">Vlastnosti položky</legend>
        <Tile icon={Banknote} label="Cena">
          <InputGroup>
            <Input
              value={draft.amount ? Number(parseAmount(draft.amount)).toLocaleString('cs-CZ') : ''}
              inputMode="numeric"
              readOnly={!canEdit}
              aria-label="Cena"
              placeholder="Zatím nevíme"
              onChange={(event) => set({ amount: event.target.value.replace(/\D/g, '') })}
              className={cn(softControl, 'tabular-nums')}
            />
            <InputAddon side="end">Kč</InputAddon>
          </InputGroup>
          {paid > 0 ? (
            <span className="block px-1 text-xs text-muted-foreground">
              Zaplaceno {formatMoney(paid)}
              {amount > paid ? ` · zbývá ${formatMoney(amount - paid)}` : ''}
            </span>
          ) : null}
        </Tile>

        <Tile icon={BadgeCheck} label="Cena je">
          <Segmented
            label="Cena je"
            options={[
              { value: 'estimate', label: 'Odhad' },
              { value: 'agreed', label: 'Dohodnutá' },
            ]}
            value={draft.estimate ? 'estimate' : 'agreed'}
            onChange={(value) => set({ estimate: value === 'estimate' })}
            disabled={!canEdit}
          />
          <span className="block px-1 text-xs text-muted-foreground">
            {draft.estimate
              ? 'Počítá se do plánu, dokud nemáte nabídku.'
              : 'Potvrzená nabídkou nebo smlouvou.'}
          </span>
        </Tile>

        <Tile icon={FolderOpen} label="Kategorie" wide>
          <Select
            variant="soft"
            className={softControl}
            value={supplier?.category ?? draft.category}
            disabled={Boolean(supplier)}
            onValueChange={(category) => set({ category })}
          >
            {categoryOrder.map((category) => (
              <SelectItem key={category} value={category}>
                {categoryLabel(category)}
              </SelectItem>
            ))}
          </Select>
          {supplier ? (
            <span className="block px-1 text-xs text-muted-foreground">
              Podle dodavatele. Změníte ji v Mých dodavatelích.
            </span>
          ) : null}
        </Tile>
      </fieldset>

      <section className="space-y-2.5">
        <h3 className="text-sm font-semibold">Dodavatel</h3>
        {supplier ? (
          <>
            <LinkedRecord
              module="suppliers"
              title={supplier.name}
              meta={supplierCategoryLabels[supplier.category] ?? 'Dodavatel'}
              actionLabel="Otevřít"
              onAction={() => onOpenSupplier(supplier.id)}
            />
            {canEdit ? (
              <Button
                appearance="ghost"
                tone="neutral"
                size="sm"
                className="-ml-3"
                onClick={() => set({ supplierId: undefined, title: draft.title || supplier.name })}
              >
                Odpojit od dodavatele
              </Button>
            ) : null}
          </>
        ) : canEdit ? (
          <>
            <Select
              variant="soft"
              className={softControl}
              aria-label="Dodavatel"
              value={NO_SUPPLIER}
              onValueChange={(value) =>
                set(
                  value === NO_SUPPLIER
                    ? { supplierId: undefined }
                    : {
                        supplierId: value,
                        category:
                          data.suppliers.find((item) => item.id === value)?.category ??
                          draft.category,
                      },
                )
              }
            >
              <SelectItem value={NO_SUPPLIER}>Bez dodavatele</SelectItem>
              {available.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name} · {supplierCategoryLabels[item.category] ?? 'Dodavatel'}
                </SelectItem>
              ))}
            </Select>
            <p className="px-1 text-xs text-muted-foreground">
              Vyberte z Mých dodavatelů. Název položky se pak bere od dodavatele.
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Bez dodavatele</p>
        )}
      </section>

      <section className="space-y-2.5">
        <h3 className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <NotebookPen aria-hidden="true" className="size-4" /> Poznámky
        </h3>
        <Textarea
          value={draft.note}
          maxLength={3000}
          readOnly={!canEdit}
          placeholder={
            canEdit ? 'Co je v ceně, číslo smlouvy, na co si dát pozor…' : 'Bez poznámek'
          }
          onChange={(event) => set({ note: event.target.value })}
          className="min-h-28 border-transparent bg-muted/60 hover:bg-muted focus:bg-card"
        />
      </section>
    </div>
  )
}

/** One property: its label above, its control always live (no extra click to edit). */
function Tile({
  icon: Icon,
  label,
  wide = false,
  children,
}: {
  icon: typeof Banknote
  label: string
  wide?: boolean
  children: ReactNode
}) {
  return (
    <div className={cn('space-y-2.5', wide && 'col-span-2')}>
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Icon className="size-4" aria-hidden="true" />
        {label}
      </span>
      <div className="space-y-1">{children}</div>
    </div>
  )
}
